import React, { useEffect, useRef } from 'react';
import { View, Platform } from 'react-native';

interface GoogleAdSenseAdProps {
  adSlot?: string;
  adFormat?: string;
  adStyle?: object;
  fullWidthResponsive?: boolean;
  className?: string;
}

/**
 * Google AdSense Ad Component
 * Properly initializes and renders Google AdSense ad units
 * Works with the Google AdSense script already loaded in index.html
 */
const GoogleAdSenseAd: React.FC<GoogleAdSenseAdProps> = ({
  adSlot,
  adFormat = 'auto',
  adStyle = {},
  fullWidthResponsive = true,
  className = ''
}) => {
  const adContainerRef = useRef<HTMLDivElement>(null);
  const adInitialized = useRef(false);

  useEffect(() => {
    // Only initialize on web platform
    if (Platform.OS !== 'web') {
      return;
    }

    // Function to initialize the ad
    const initializeAd = () => {
      if (adInitialized.current || !adContainerRef.current || Platform.OS !== 'web') {
        return;
      }

      try {
        // Find the ins element within the container
        const insElement = adContainerRef.current.querySelector('ins.adsbygoogle') as HTMLElement;

        if (insElement && (window as any).adsbygoogle) {
          // Push the ad configuration to adsbygoogle
          ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
          adInitialized.current = true;
        }
      } catch (error) {
        console.error('Error initializing Google AdSense ad:', error);
      }
    };

    // Check if adsbygoogle is available
    if (typeof window !== 'undefined' && (window as any).adsbygoogle) {
      // Script is already loaded, initialize after a short delay to ensure DOM is ready
      const timer = setTimeout(initializeAd, 100);
      return () => clearTimeout(timer);
    } else {
      // Wait for the script to load
      const checkAdsbyGoogle = setInterval(() => {
        if ((window as any).adsbygoogle) {
          clearInterval(checkAdsbyGoogle);
          initializeAd();
        }
      }, 100);

      // Timeout after 5 seconds
      const timeout = setTimeout(() => {
        clearInterval(checkAdsbyGoogle);
      }, 5000);

      return () => {
        clearInterval(checkAdsbyGoogle);
        clearTimeout(timeout);
      };
    }
  }, []);

  // Don't render on native platforms
  if (Platform.OS !== 'web') {
    return null;
  }

  const defaultStyle: React.CSSProperties = {
    display: 'block',
    textAlign: 'center',
    minHeight: 100,
    ...adStyle
  };

  // Build style string for inline styles
  const styleString = Object.entries(defaultStyle)
    .map(([key, value]) => {
      const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
      return `${cssKey}: ${value}`;
    })
    .join('; ');

  // Create the ad HTML structure
  const adHtml = `
    <ins
      class="adsbygoogle ${className}"
      style="${styleString}"
      data-ad-client="ca-pub-8800527179103112"
      ${adSlot ? `data-ad-slot="${adSlot}"` : ''}
      data-ad-format="${adFormat}"
      data-full-width-responsive="${fullWidthResponsive ? 'true' : 'false'}"
    ></ins>
  `;

  return (
    <View style={{ width: '100%', alignItems: 'center', marginVertical: 16 }}>
      {/* @ts-ignore - Using web-specific div element */}
      <div
        ref={adContainerRef}
        dangerouslySetInnerHTML={{ __html: adHtml }}
        style={{ width: '100%', minHeight: 250 }}
      />
    </View>
  );
};

export default GoogleAdSenseAd;

